import { Button, IconButton, Popover, Stack, Typography } from "@mui/material";
import { BsThreeDotsVertical, BsTrash } from "react-icons/bs";
import useData from "src/hooks/useApi";
import useMessage from "src/hooks/useMessage";
import React from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ProgressStatusEnum } from "../../enums/allBidsPageEnums";
import Big from "big.js";
import {
    AuctionStatusEnum,
    AuctionTableData,
} from "../../types/postAuctionTypes";
import { BackorderData, SelectedBid } from "src/types/allBidsPageTypes";
import { allBidsRowMenuStyles } from "./modelStyles";
import { TopUpOptions } from "../balance/WalletTopUp";
import { isMoreThanOneHourInFuture } from "src/utils/helperFunctions";

type BackorderActionMenu = {
    data: BackorderData;
    deleteBid: (selectedBid: SelectedBid) => void;
};

export const BackordersActionMenu = (props: BackorderActionMenu) => {
    const { data, deleteBid } = props;

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
        null,
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                aria-describedby={"backorder-action-menu"}
                onClick={handleClick}
            >
                <BsThreeDotsVertical style={{ fontSize: "14px" }} />
            </IconButton>
            <Popover
                id={"backorder-action-menu"}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Stack sx={allBidsRowMenuStyles.list}>
                    <Button
                        sx={allBidsRowMenuStyles.listButton}
                        onClick={() =>
                            deleteBid({
                                bidId: data.id,
                                domainName: data.domainName,
                                priority: data.priority,
                                bidAmount: Number(data.amount),
                                isPreBackorder: data.isPreBackorder,
                            })
                        }
                        disabled={
                            data.isLocked ||
                            data.status == ProgressStatusEnum.PENDING ||
                            !isMoreThanOneHourInFuture(data.dropTime)
                        }
                    >
                        <BsTrash
                            style={{ color: "#FF4D4F", fontSize: "13px" }}
                        />
                        <Typography sx={allBidsRowMenuStyles.listText}>
                            Delete
                        </Typography>
                    </Button>
                </Stack>
            </Popover>
        </>
    );
};

type AuctionActionMenu = {
    data: AuctionTableData;
    topUp: (message: string, defaultAmount: TopUpOptions) => void;
};

export const AuctionsActionMenu = (props: AuctionActionMenu) => {
    const { data, topUp } = props;

    const { fetchData } = useData();
    const { showError } = useMessage();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
        null,
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    async function handleTopUp() {
        try {
            const response = await fetchData("/bid/paymentPendingBalance");
            let defaultAmount: TopUpOptions;
            if (response > 0) {
                const amount = new Big(response);
                switch (true) {
                    case amount.lte(20):
                        defaultAmount = 20;
                        break;
                    case amount.lte(50):
                        defaultAmount = 50;
                        break;
                    case amount.lte(100):
                        defaultAmount = 100;
                        break;
                    case amount.lte(200):
                        defaultAmount = 200;
                        break;
                    default:
                        defaultAmount = 500;
                        break;
                }
                topUp(
                    `Your total payment pending amount is ${amount.toFixed(
                        2,
                    )}$. You can Top-up your account  using the below options`,
                    defaultAmount,
                );
            } else {
                throw new Error("request failed");
            }
        } catch (error) {
            showError("Error in getting payment pending balance");
            console.log(error);
        }
    }

    return (
        <>
            <IconButton
                aria-describedby={"backorder-action-menu"}
                onClick={handleClick}
            >
                <BsThreeDotsVertical style={{ fontSize: "14px" }} />
            </IconButton>
            <Popover
                id={"backorder-action-menu"}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Stack sx={allBidsRowMenuStyles.list}>
                    <Button
                        sx={allBidsRowMenuStyles.listButton}
                        onClick={handleTopUp}
                        disabled={
                            data.auctionStatus !=
                            AuctionStatusEnum.PAYMENT_PENDING
                        }
                    >
                        <AddCircleOutlineIcon
                            style={{ color: "#21B2E7", fontSize: "13px" }}
                        />
                        <Typography sx={allBidsRowMenuStyles.listText}>
                            Top-up
                        </Typography>
                    </Button>
                </Stack>
            </Popover>
        </>
    );
};
